import { useEffect } from 'react';

import type { ButtonProps } from '@/components/LegacyButton';
import { Price } from '@/components/Price';
import type { MoneyFragment } from '@/graphql/saleor/api';
import type { CtaType, Maybe } from '@/lib/types';

import { CTA as ProductDetailCta } from '../CTA';
import { useBottomFixedOffset } from './useBottomFixedOffset';

import * as S from './StickyPurchaseBar.styles';

// Matches the bar's min-height (StickyPurchaseBar.styles.tsx). Used to reserve
// space at the bottom of the page so the fixed bar never occludes the last
// content (footer / service perks) when scrolled to the bottom.
const BAR_HEIGHT = '84px';

export interface StickyPurchaseBarProps extends Pick<
  ButtonProps,
  'isProcessing' | 'onClick'
> {
  cta: CtaType | undefined;
  image?: Maybe<{ alt?: string | null; url: string }>;
  loading: boolean;
  name: string;
  price?: Maybe<MoneyFragment>;
  visible: boolean;
}

/**
 * PDP sticky conversion bar. Mirrors the on-page purchase CTA (same label and
 * click behaviour for every availability state) by reusing the shared `CTA`
 * component with the same `cta` value and `onClick` handler. Shown when the
 * primary purchase area scrolls out of view.
 */
export const StickyPurchaseBar = ({
  cta,
  image,
  isProcessing,
  loading,
  name,
  onClick,
  price,
  visible,
}: StickyPurchaseBarProps) => {
  // Offset the bar above any bottom-fixed third-party UI (cookie banner / chat
  // bar) so it never overlaps them; also drives the page spacer below.
  const bottomOffset = useBottomFixedOffset(visible);

  // Reserve bottom space while the bar is shown so it (and anything it stacks
  // above) doesn't cover page content at the bottom of the scroll. Restored on
  // hide/unmount.
  useEffect(() => {
    if (!visible) {
      return;
    }
    const previous = document.body.style.paddingBottom;
    document.body.style.paddingBottom = `calc(${BAR_HEIGHT} + ${bottomOffset}px)`;
    return () => {
      document.body.style.paddingBottom = previous;
    };
  }, [visible, bottomOffset]);

  if (!visible) {
    return null;
  }

  return (
    <S.Wrapper aria-label={name} data-testid='StickyPurchaseBar' role='region'>
      <S.Content>
        <S.Recap data-testid='StickyPurchaseBarRecap'>
          {image?.url && (
            <S.ImageWrapper>
              <S.ProductImage
                alt={image.alt ?? name}
                data-testid='StickyPurchaseBarImage'
                fill
                sizes='56px'
                src={image.url}
              />
            </S.ImageWrapper>
          )}
          <S.Name data-testid='StickyPurchaseBarName' scale='h5'>
            {name}
          </S.Name>
        </S.Recap>

        <S.Action data-testid='StickyPurchaseBarAction'>
          <S.PriceWrapper data-testid='StickyPurchaseBarPrice'>
            <Price price={price} size='m' />
          </S.PriceWrapper>
          <S.CtaWrapper data-testid='StickyPurchaseBarCta'>
            <ProductDetailCta
              cta={cta}
              isProcessing={isProcessing}
              loading={loading}
              onClick={onClick}
            />
          </S.CtaWrapper>
        </S.Action>
      </S.Content>
    </S.Wrapper>
  );
};

StickyPurchaseBar.displayName = 'StickyPurchaseBar';
